import { ArrowPathIcon, CheckIcon, MapPinIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    formatEventHeadline,
    formattedDateTimeDefault,
    genEntityId,
    UserId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { AgendaVisitOutcomePanel } from "components/marketing-plan/agenda-visit-outcome-panel";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { trpc } from "components/rpc/trpc";
import {
    ActivityTypeOption,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { TaskFormV2 } from "components/tasks/v2/task-form-v2";
import { Taskv2 } from "components/tasks/v2/types";
import { BlankSlate } from "components/ui/blank-slate";
import { Checkbox } from "components/ui/checkbox";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { CompletingListItem } from "components/ui/list-motion";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryPlusButton } from "components/ui/primary-plus-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { CalendarEventDto, VisitParent } from "components/visits/types";
import { VisitEditor } from "components/visits/visit-editor";
import { AnimatePresence } from "framer-motion";
import {
    GetTasksForEntityQuery,
    TaskBasicsFragment,
    useCreateTaskMutation,
    useDeleteTaskMutation,
    useGetTasksForEntityQuery,
    useUpdateTaskMutation,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import { useCurrentUserId, useUsers } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { taskEntityForParent } from "./activity-parent";
import {
    ActivityRowBody,
    ActivityRowChipLine,
    ActivityRowEditButton,
    activityRowIconClasses,
    ActivityRowsList,
} from "./activity-rows";
import {
    buildScheduledActivityRows,
    isScheduledTodoOverdue,
} from "./scheduled-activities-rows";

type TaskDto = GetTasksForEntityQuery["getTasksForEntity"][0];

const activityTypeFor = (
    activityTypes: ActivityTypeOption[],
    taskTypeId: string | null | undefined,
): ActivityTypeOption | null =>
    taskTypeId ? (activityTypes.find(option => option.id === taskTypeId) ?? null) : null;

const todoDueLabel = (todo: TaskDto): string => {
    if (todo.dueDate) {
        const parsed = CalendarDate.parse(todo.dueDate);
        if (parsed.hasValue) {
            return `Due ${parsed.get().humanFriendly()}`;
        }
    }
    if (todo.dueDateTime) {
        return `Due ${formattedDateTimeDefault(todo.dueDateTime)}`;
    }
    return "No due date";
};

interface ScheduledActivitiesCardProps {
    parent: VisitParent;
}

export const ScheduledActivitiesCard: React.FC<ScheduledActivitiesCardProps> = props => {
    const { parent } = props;
    const trpcClient = trpc.useTRPC();
    const now = new Date();
    const visitsQueryOptions = React.useMemo(
        () =>
            parent.kind === "organization"
                ? trpcClient.calendarEvents.listByBusiness.queryOptions({
                      businessId: parent.id,
                  })
                : trpcClient.calendarEvents.listByParent.queryOptions({
                      parentId: parent.id,
                  }),
        [trpcClient, parent],
    );
    const visitsQuery = useQuery(visitsQueryOptions);
    const activityTypes = useActivityTypeOptions();
    const tasksQuery = useGetTasksForEntityQuery({ parentId: parent.id });
    const updateTask = useUpdateTaskMutation();
    const createTask = useCreateTaskMutation();
    const deleteTask = useDeleteTaskMutation();
    const refreshAgenda = useAgendaRefresh();
    const users = useUsers().users;
    const currentUserId = useCurrentUserId();
    const [editingTodo, setEditingTodo] = React.useState<TaskDto | null>(null);
    const [editingVisit, setEditingVisit] = React.useState<CalendarEventDto | null>(null);
    const [loggingOutcomeVisit, setLoggingOutcomeVisit] =
        React.useState<CalendarEventDto | null>(null);
    const [schedulingVisit, setSchedulingVisit] = React.useState(false);
    const [creatingTodo, setCreatingTodo] = React.useState(false);
    const [keepCompletedIds, setKeepCompletedIds] = React.useState<string[]>([]);
    const queryClient = useQueryClient();

    const patchTodoLocally = React.useCallback(
        (todoId: string, patch: Pick<TaskDto, "status" | "completedAt" | "done">) => {
            queryClient.setQueryData<GetTasksForEntityQuery>(
                ["getTasksForEntity", { parentId: parent.id }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        draft.getTasksForEntity = draft.getTasksForEntity.map(t =>
                            t.id === todoId ? { ...t, ...patch } : t,
                        );
                    });
                },
            );
        },
        [queryClient, parent.id],
    );

    const completeTodo = React.useCallback(
        async (todo: TaskDto) => {
            handleEvent("task-complete", { source: "detail-overview" });
            setKeepCompletedIds(ids => (ids.includes(todo.id) ? ids : [...ids, todo.id]));
            const completedAt = new Date().toISOString();
            patchTodoLocally(todo.id, { status: "done", completedAt, done: true });
            await updateTask.mutateAsync({
                taskId: todo.id,
                params: {
                    status: "done",
                    completedAt,
                },
            });
            refreshAgenda();
        },
        [updateTask, refreshAgenda, patchTodoLocally],
    );

    const reopenTodo = React.useCallback(
        async (todo: TaskDto) => {
            setKeepCompletedIds(ids => ids.filter(id => id !== todo.id));
            patchTodoLocally(todo.id, { status: "new", completedAt: null, done: false });
            await updateTask.mutateAsync({
                taskId: todo.id,
                params: {
                    status: "new",
                    completedAt: null,
                },
            });
            refreshAgenda();
        },
        [updateTask, refreshAgenda, patchTodoLocally],
    );

    const deleteTodo = React.useCallback(
        async (todo: TaskDto) => {
            await deleteTask.mutateAsync({ taskId: todo.id });
            refreshAgenda();
        },
        [deleteTask, refreshAgenda],
    );

    const closeEditors = React.useCallback(() => {
        setEditingTodo(null);
        setEditingVisit(null);
    }, []);

    const editingInitial: Taskv2 | null = React.useMemo(() => {
        if (!editingTodo) {
            return null;
        }
        return {
            entity: taskEntityForParent(parent),
            task: {
                __typename: "Task" as const,
                ...editingTodo,
            },
        };
    }, [editingTodo, parent]);

    const newTodoInitial: Taskv2 = React.useMemo(
        () => ({
            entity: taskEntityForParent(parent),
            task: {
                __typename: "Task" as const,
                done: false,
                id: genEntityId(),
                parentId: parent.id,
                status: "new",
                completedAt: null as TaskBasicsFragment["completedAt"],
                dueDate: CalendarDate.todayNoTime.toString(),
                dueDateTime: null as TaskBasicsFragment["dueDateTime"],
                ownerId: currentUserId,
                taskTypeId: null as TaskBasicsFragment["taskTypeId"],
                taskType: null as TaskBasicsFragment["taskType"],
                text: "",
                notes: "",
                recurrencePattern: null as TaskBasicsFragment["recurrencePattern"],
                recurrenceInterval: null as TaskBasicsFragment["recurrenceInterval"],
                recurrenceDaysOfWeek: null as TaskBasicsFragment["recurrenceDaysOfWeek"],
                recurrenceDayOfMonth: null as TaskBasicsFragment["recurrenceDayOfMonth"],
            },
        }),
        [parent, currentUserId],
    );

    const rows = buildScheduledActivityRows(
        visitsQuery.data ?? [],
        tasksQuery.data?.getTasksForEntity ?? [],
        { now, keepCompletedIds },
    );
    const hasCompletedRows = rows.some(row => row.kind === "todo" && row.todo.done);

    const ownerName = (ownerId: UserId | null) =>
        ownerId ? (users[ownerId]?.name ?? null) : null;

    return (
        <InputFormBlock>
            <Header
                action={
                    <div className="flex items-center gap-2">
                        <PrimaryPlusButton onClick={() => setSchedulingVisit(true)}>
                            Schedule Visit
                        </PrimaryPlusButton>
                        <PrimaryPlusButton onClick={() => setCreatingTodo(true)}>
                            Todo
                        </PrimaryPlusButton>
                    </div>
                }>
                Planned Activities
            </Header>
            {rows.length === 0 ? (
                <BlankSlate>
                    Nothing scheduled yet. Schedule a visit or add a todo and it will show
                    up here.
                </BlankSlate>
            ) : null}
            <ActivityRowsList className="" testId="planned-activities-rows">
                <AnimatePresence initial={false}>
                    {rows.map(row => {
                        const rowType = activityTypeFor(
                            activityTypes,
                            row.kind === "visit"
                                ? row.visit.activityTypeId
                                : row.todo.taskTypeId,
                        );
                        return row.kind === "todo" ? (
                            <CompletingListItem
                                key={row.key}
                                isDone={row.todo.done}
                                className="">
                                <ActivityRowBody
                                    leading={
                                        <Checkbox
                                            aria-label={`Complete ${
                                                row.todo.text || "untitled todo"
                                            }`}
                                            checked={row.todo.done}
                                            onChange={() =>
                                                row.todo.done
                                                    ? reopenTodo(row.todo)
                                                    : completeTodo(row.todo)
                                            }
                                        />
                                    }
                                    actions={
                                        <ActivityRowEditButton
                                            onClick={() => setEditingTodo(row.todo)}
                                        />
                                    }>
                                    <span
                                        className={
                                            row.todo.done
                                                ? "font-medium text-gray-500 line-through"
                                                : "font-medium text-gray-900"
                                        }>
                                        {row.todo.text || "(untitled)"}
                                    </span>
                                    <span
                                        className={
                                            !row.todo.done &&
                                            isScheduledTodoOverdue(row.when, now)
                                                ? "text-red-600"
                                                : "text-gray-500"
                                        }>
                                        {" · "}
                                        {todoDueLabel(row.todo)}
                                    </span>
                                    {rowType ? (
                                        <ActivityRowChipLine
                                            category={rowType.category}
                                            label={rowType.name}
                                        />
                                    ) : null}
                                    {row.todo.notes ? (
                                        <div className="text-gray-500 truncate">
                                            {row.todo.notes}
                                        </div>
                                    ) : null}
                                    <div className="text-xs text-gray-500">
                                        Assigned to{" "}
                                        {ownerName(row.todo.ownerId) ?? "no one"}
                                        {row.todo.recurrencePattern &&
                                        row.todo.recurrencePattern !== "None" ? (
                                            <span className="inline-flex items-center gap-0.5 ml-2">
                                                <ArrowPathIcon
                                                    aria-hidden="true"
                                                    className="h-3.5 w-3.5"
                                                />
                                                {row.todo.recurrencePattern}
                                            </span>
                                        ) : null}
                                    </div>
                                </ActivityRowBody>
                            </CompletingListItem>
                        ) : (
                            <CompletingListItem key={row.key} isDone={false} className="">
                                <ActivityRowBody
                                    leading={
                                        <MapPinIcon
                                            aria-hidden="true"
                                            className={activityRowIconClasses}
                                        />
                                    }
                                    actions={
                                        <>
                                            <SecondaryCompactButton
                                                className="items-center gap-1"
                                                onClick={() =>
                                                    setLoggingOutcomeVisit(row.visit)
                                                }>
                                                <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                                Log outcome
                                            </SecondaryCompactButton>
                                            <ActivityRowEditButton
                                                onClick={() => setEditingVisit(row.visit)}
                                            />
                                        </>
                                    }>
                                    <span className="font-medium text-gray-900">
                                        {row.visit.title?.trim() || "Visit"}
                                    </span>
                                    <span className="text-gray-500">
                                        {" · "}
                                        {formatEventHeadline(
                                            row.visit.eventDate,
                                            row.visit.eventTime,
                                            row.visit.eventDuration,
                                        )}
                                    </span>
                                    {row.awaitingOutcome ? (
                                        <>
                                            {" · "}
                                            <span className="font-medium text-red-600">
                                                Awaiting outcome
                                            </span>
                                        </>
                                    ) : null}
                                    {rowType ? (
                                        <ActivityRowChipLine
                                            category="visit"
                                            label={rowType.name}
                                        />
                                    ) : null}
                                    {ownerName(row.visit.userId) ? (
                                        <div className="text-xs text-gray-500">
                                            Assigned to {ownerName(row.visit.userId)}
                                        </div>
                                    ) : null}
                                </ActivityRowBody>
                            </CompletingListItem>
                        );
                    })}
                </AnimatePresence>
            </ActivityRowsList>
            {hasCompletedRows ? (
                <InteractiveTextLink
                    className="mt-2 text-sm"
                    onClick={() => setKeepCompletedIds([])}>
                    Clear completed items
                </InteractiveTextLink>
            ) : null}
            <ResponsivePanel
                isOpen={editingTodo !== null}
                type={PanelType.medium}
                headerText="Edit todo"
                onDismiss={closeEditors}>
                {editingInitial ? (
                    <TaskFormV2
                        initial={editingInitial}
                        mode="update"
                        onClose={closeEditors}
                        saveTask={async (_parentId, payload) => {
                            if (!editingTodo) {
                                return;
                            }
                            await updateTask.mutateAsync({
                                taskId: editingTodo.id,
                                params: payload,
                            });
                            closeEditors();
                            refreshAgenda();
                        }}
                        onDelete={async () => {
                            if (!editingTodo) {
                                return;
                            }
                            await deleteTodo(editingTodo);
                            closeEditors();
                        }}
                        entityLinkDisabled
                    />
                ) : null}
            </ResponsivePanel>
            <ResponsivePanel
                isOpen={creatingTodo}
                type={PanelType.medium}
                headerText="New todo"
                onDismiss={() => setCreatingTodo(false)}>
                <TaskFormV2
                    initial={newTodoInitial}
                    mode="create"
                    onClose={() => setCreatingTodo(false)}
                    saveTask={async (_parentId, payload) => {
                        await createTask.mutateAsync({
                            parentId: parent.id,
                            params: payload,
                        });
                        handleEvent("task-create", { source: "detail-overview" });
                        refreshAgenda();
                        setCreatingTodo(false);
                    }}
                    entityLinkDisabled
                />
            </ResponsivePanel>
            {schedulingVisit ? (
                <VisitEditor
                    mode="new"
                    parent={parent}
                    activityTypeId={null}
                    onSaved={() => {
                        refreshAgenda();
                        setSchedulingVisit(false);
                    }}
                    onClose={() => setSchedulingVisit(false)}
                />
            ) : null}
            {editingVisit ? (
                <VisitEditor
                    mode="edit"
                    visit={editingVisit}
                    onSaved={() => {
                        closeEditors();
                        refreshAgenda();
                    }}
                    onClose={closeEditors}
                />
            ) : null}
            {loggingOutcomeVisit ? (
                <AgendaVisitOutcomePanel
                    visit={loggingOutcomeVisit}
                    onChanged={() => refreshAgenda()}
                    onClose={() => setLoggingOutcomeVisit(null)}
                />
            ) : null}
        </InputFormBlock>
    );
};
